import React, { FunctionComponent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from "@tanstack/react-query";
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next'
import { useTheme } from '@mui/material/styles';

import { fetch_all_with_count } from "../../../../lib/v31lib";
import { loginAtom } from '../../../../lib/auth';
import { currentPracticeAtom } from '../../../../lib/practice';

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';

import {
  Chip,
  Divider,
  Grid,
  Paper,
} from '@mui/material';

import {
  Button,
  Typography
} from '../../../../components/v2/styled';

import { User } from '../../../../models/User';
import { Medication } from '../../../../models/Medication';

const { DateTime } = require("luxon");

import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';

// Msbo's exact grid order (newest first, open supplement/medication rows on
// top) — the widget shows the same top slice, per_page'd by the amount setting.
const MEDICATION_ORDER = "IF(med_type='supplement' OR med_type='medicatie' OR ISNULL(med_type)=1, ISNULL(med_end), 0) DESC, IF(med_end>NOW(), 1, 0) DESC, med_start DESC, med_end DESC";

// Msbo's med_type chip mapping, same colours, compacted font (0.75rem vs the
// grid's 0.9rem). Unknown/missing types render no chip, like the grid.
const MED_TYPE_CHIPS: {[med_type: string]: {color: string, labelKey: string}} = {
  medicatie: {color: '#8FB3E0', labelKey: 'client.models.medications.med_types.medication'},
  supplement: {color: '#8FB3E0', labelKey: 'client.models.medications.med_types.supplement'},
  treatment: {color: '#F7C48A', labelKey: 'client.models.medications.med_types.treatment'},
  research: {color: '#B3A6D6', labelKey: 'client.models.medications.med_types.research'},
  operation: {color: '#F7C48A', labelKey: 'client.models.medications.med_types.operation'},
  complaint: {color: '#F2A08C', labelKey: 'client.models.medications.med_types.complaint'},
  important_event: {color: '#F2A08C', labelKey: 'client.models.medications.med_types.important_event'},
  diet: {color: '#97BE6B', labelKey: 'client.models.medications.med_types.diet'},
};

type Props = {
  object: User;
}

export const CoachUserWidgetsTimeline: FunctionComponent<Props> = ({
  object
}) => {
  const [login, setLogin] = useAtom(loginAtom);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);
  const theme = useTheme();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(['translations']);

  // Pre-format dates as strings (Luxon, same output as <Dt f="dd/LL/y" />) so
  // start + separator + end live in ONE Typography string child.
  const fmtDay = (d?: string) => {
    if (!d) return '';
    return DateTime.fromISO(d).setZone("Europe/Brussels").toFormat("dd/LL/y", {locale: i18n.resolvedLanguage || 'nl'});
  }

  // Coach-level widget settings: composite { cta, amount } under one value
  // key. Stored on login.settings.userdetails_widgets.settings.
  const widgetSettings = login?.settings?.userdetail_widgets?.settings;
  const cfg = widgetSettings?.[HelpersClientWidgets.timelineValueKey];
  const showCta = HelpersClientWidgets.getTimelineCtaEnabled(cfg);
  const amount = HelpersClientWidgets.getTimelineAmount(cfg);

  // Msbo's query, adapted: pipe-syntax practice projection, per_page = the
  // amount setting, keyed on the viewed client.
  const {
    data: medications,
    isSuccess,
  } = useQuery({
    queryKey: ["client", "timeline_widget", object?.id],
    queryFn: () =>
      fetch_all_with_count<Medication>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'medication',
          fields: ['id', 'med_start', 'med_end', 'name', 'med_type', 'practice|id/name'],
          per_page: amount,
          page: 0,
          order: MEDICATION_ORDER,
          skip_order_check: 1,
          filter: {
            search: '',
            advanced: {
              user_id: object?.id,
              practice_id: currentPractice?.id
            }
          },
          practice_id: currentPractice?.id
        },
        login
      ),
    select: (d) => d.result,
    enabled: !!object?.id && !!login?.id,
  });

  const rows = medications || [];

  return <Paper sx={{padding: 2, marginTop: 1, position: 'relative'}}>
    {/* CTA: small text button top-right, absolute in the title row. Gated by
        the coach's `cta` setting. No premium lock, the file page's tabs gate
        their own modules. */}
    {showCta && <Button
      label={t("coach.general.actions.to_msbo")}
      id="goto_msbo"
      text
      size="small"
      startIcon={<ArrowRightAltIcon />}
      sx={{position: 'absolute', top: 4, right: 4, marginTop: 0, zIndex: 2}}
      onClick={() => {
        navigate(`/user/${object.id}/file`, {state: {tab: 4}});
      }}
    />}

    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography variant="h5" html>{t("coach.submenu.detail.timeline")}</Typography>
        <Divider />
      </Grid>

      {isSuccess && rows.length === 0 && <Grid item xs={12}>
        <Typography sx={{color: theme.palette.secondary.main, fontSize: '0.7rem', fontStyle: 'italic'}} html>{t("general.no_results_found")}</Typography>
      </Grid>}

      {/* Medication rows: chip + name on the first line, dates caption below,
          a Divider between consecutive rows. Newest first (server order). */}
      {rows.map((row, idx) => {
        const chip = !!row.med_type && MED_TYPE_CHIPS[row.med_type];
        return <React.Fragment key={row.id}>
          <Grid item xs={12} sx={{display: 'flex', alignItems: 'center', gap: 0.5, position: 'relative'}}>
            {!!chip && <Chip sx={{fontSize: '0.75rem', backgroundColor: chip.color, position: 'absolute', top: 4, right: 0}} label={t(chip.labelKey)} />}
            <Typography variant="subtitle2" sx={{flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{row.name}</Typography>
          </Grid>
          <Grid item xs={12} sx={{marginTop: -1}}>
            <Typography variant="caption" sx={{color: 'text.secondary'}}>
              {`${fmtDay(row.med_start)}${!!row.med_end ? ` - ${fmtDay(row.med_end)}` : ''}`}
            </Typography>
          </Grid>
          {idx < rows.length - 1 && <Grid item xs={12}>
            <Divider />
          </Grid>}
        </React.Fragment>;
      })}
    </Grid>
  </Paper>;
}
